/*
  Inside Nouvel Heritage · scrollspy + reveal
  ===========================================
  - Highlights the active chapter in the floating spine
  - Swaps spine color when we enter a dark chapter (Ch 03)
  - Reveals content blocks as they enter the viewport
*/

(function () {
  'use strict';

  function initSpine() {
    var spine = document.querySelector('[data-inside-nh-spine]');
    if (!spine) return;
    var links = spine.querySelectorAll('a[data-target]');
    if (!links.length) return;

    var chapters = [];
    links.forEach(function (link) {
      var target = document.getElementById(link.dataset.target);
      if (target) {
        chapters.push({ id: link.dataset.target, el: target, link: link });
      } else {
        link.remove();
      }
    });
    if (!chapters.length) return;

    var darkCount = 0;

    /* Tone detection via IntersectionObserver.
       We observe each chapter with a rootMargin that collapses the viewport to a
       thin horizontal slice at its vertical middle — exactly where the fixed
       spine sits. As soon as a dark-tone chapter crosses that slice, we flip the
       spine into dark mode; when it leaves, we flip back. This replaces the old
       scroll+getBoundingClientRect math which could miss the transition if the
       viewport middle landed outside a section for one frame. */
    if ('IntersectionObserver' in window) {
      var toneObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.target.dataset.tone !== 'dark') return;
          if (entry.isIntersecting) {
            darkCount += 1;
          } else {
            darkCount = Math.max(0, darkCount - 1);
          }
        });
        spine.classList.toggle('is-on-dark', darkCount > 0);
      }, {
        /* top% / bottom% — both negative so we observe a 2px-tall slice
           centered at the viewport's vertical middle. */
        rootMargin: '-50% 0px -50% 0px',
        threshold: 0
      });
      chapters.forEach(function (c) { toneObserver.observe(c.el); });
    }

    function update() {
      var y = window.scrollY || window.pageYOffset;
      var vh = window.innerHeight;
      spine.classList.toggle('is-visible', y > 400);

      /* triggerY (viewport third) decides which chapter is "active" in the rail. */
      var triggerY = y + vh / 3;
      var activeId = null;

      chapters.forEach(function (c) {
        var rect = c.el.getBoundingClientRect();
        var top = rect.top + y;
        var bottom = top + rect.height;
        if (triggerY >= top && triggerY < bottom) activeId = c.id;
      });

      chapters.forEach(function (c) {
        var isActive = c.id === activeId;
        c.link.classList.toggle('is-active', isActive);
        if (isActive) {
          c.link.setAttribute('aria-current', 'location');
        } else {
          c.link.removeAttribute('aria-current');
        }
      });
    }

    var ticking = false;
    window.addEventListener('scroll', function () {
      if (!ticking) {
        window.requestAnimationFrame(function () {
          update();
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });

    update();
  }

  function initVideoControls() {
    var wrappers = document.querySelectorAll('[data-nh-video]');
    if (!wrappers.length) return;

    var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    wrappers.forEach(function (wrapper) {
      var video = wrapper.querySelector('[data-nh-video-el]');
      var toggle = wrapper.querySelector('[data-nh-video-toggle]');
      if (!video || !toggle) return;

      /* Force mute on every Inside Nouvel Heritage video. Luxury brand
         rule: ambient editorial videos never play sound. */
      try {
        video.muted = true;
        video.defaultMuted = true;
        video.volume = 0;
        video.setAttribute('muted', '');
      } catch (e) {}

      var labelPause = toggle.getAttribute('data-label-pause') || 'Pause';
      var labelPlay = toggle.getAttribute('data-label-play') || 'Play';
      var srText = toggle.querySelector('[data-nh-video-label]');

      function syncState() {
        var paused = video.paused;
        toggle.setAttribute('data-state', paused ? 'play' : 'pause');
        toggle.removeAttribute('aria-pressed');
        var label = paused ? labelPlay : labelPause;
        toggle.setAttribute('aria-label', label);
        if (srText) srText.textContent = label;
      }

      toggle.addEventListener('click', function () {
        video.setAttribute('data-nh-user-toggled', '');
        if (video.paused) {
          var p = video.play();
          if (p && typeof p.then === 'function') {
            p.then(syncState).catch(syncState);
          } else {
            syncState();
          }
        } else {
          video.pause();
          syncState();
        }
      });

      video.addEventListener('play', syncState);
      video.addEventListener('pause', syncState);

      if (reduceMotion) {
        video.removeAttribute('autoplay');
        try { video.pause(); } catch (e) {}
      }

      syncState();
    });

    /* NH-CODE-250 (2026-11-02): the chapter videos ship preload="none" with a
       poster and no native autoplay, so /pages/inside-nh no longer downloads
       every film on page load. Start each one as it approaches the viewport,
       unless the visitor prefers reduced motion or has already used its
       pause/play toggle. Same pattern as inside-nh-teaser (2026-08-04) and
       inside-nh-pdp-module (NH-CODE-101). */
    if (reduceMotion || !('IntersectionObserver' in window)) return;
    var videoIo = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var v = entry.target;
        videoIo.unobserve(v);
        if (v.hasAttribute('data-nh-user-toggled')) return;
        var p = v.play();
        if (p && typeof p.catch === 'function') p.catch(function () {});
      });
    }, { rootMargin: '200px 0px' });
    document.querySelectorAll('[data-nh-video] [data-nh-video-el]').forEach(function (v) {
      videoIo.observe(v);
    });
  }

  function initReveal() {
    var targets = document.querySelectorAll('.inside-nh-reveal');
    if (!targets.length) return;
    if (!('IntersectionObserver' in window)) {
      targets.forEach(function (t) { t.classList.add('is-in-view'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-in-view');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.15 });
    targets.forEach(function (t) { io.observe(t); });
  }

  function boot() {
    initSpine();
    initReveal();
    initVideoControls();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
